import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Typography,
  Paper,
  Box,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  InputAdornment,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ContentCopy as CopyIcon,
  LinkOff as LinkOffIcon,
  VpnKey as VpnKeyIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import AdminLayout from '../../../layouts/AdminLayout';
import unifiedApi from '../../../utils/unifiedApi';
import { useCanManageLeague } from '../../../contexts/PermissionsContext';
import { FEATURES, useFeature } from '../../../contexts/FeatureContext';

const POSITION_TYPES = [
  { value: 'OutfieldPlayer', label: 'Outfield Player' },
  { value: 'Goalkeeper', label: 'Goalkeeper' },
  { value: 'SpareOutfielder', label: 'Spare Outfielder' },
  { value: 'SpareGoalkeeper', label: 'Spare Goalkeeper' },
];

const SPARE_POSITION_TYPES = new Set(['SpareOutfielder', 'SpareGoalkeeper']);

const getPositionLabel = (value) => POSITION_TYPES.find(p => p.value === value)?.label || value;

const QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

const QUILL_FORMATS = ['bold', 'italic', 'underline', 'list', 'link'];

const isHtmlEmpty = (html) => {
  if (!html) return true;
  const stripped = html.replace(/<[^>]*>/g, '').trim();
  return stripped.length === 0;
};

const RegistrationConfigPage = () => {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const canEdit = useCanManageLeague(parseInt(leagueId));
  const sparesEnabled = useFeature(FEATURES.SPARES, leagueId);
  const addablePositionTypes = sparesEnabled
    ? POSITION_TYPES
    : POSITION_TYPES.filter(pt => !SPARE_POSITION_TYPES.has(pt.value));

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [registrationOpenDate, setRegistrationOpenDate] = useState('');  const [pendingTimeoutMinutes, setPendingTimeoutMinutes] = useState(15);
  const [blurb, setBlurb] = useState('');
  const [paymentWarningMessage, setPaymentWarningMessage] = useState('');
  const [installmentsEnabled, setInstallmentsEnabled] = useState(false);
  const [positions, setPositions] = useState([]);

  const [earlyAccessEnabled, setEarlyAccessEnabled] = useState(false);
  const [earlyAccessToken, setEarlyAccessToken] = useState(null);
  const [earlyAccessExpiry, setEarlyAccessExpiry] = useState('');
  const [leagueSlug, setLeagueSlug] = useState(null);
  const [earlyAccessBusy, setEarlyAccessBusy] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState(false);

  const quillModules = useMemo(() => QUILL_MODULES, []);
  const quillFormats = useMemo(() => QUILL_FORMATS, []);

  const earlyAccessUrl = useMemo(() => {
    if (!earlyAccessEnabled || !earlyAccessToken || !leagueSlug) return '';
    return `${window.location.origin}/leagues/${leagueSlug}/register?earlyAccess=${earlyAccessToken}`;
  }, [earlyAccessEnabled, earlyAccessToken, leagueSlug]);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const config = await unifiedApi.getRegistrationConfig(leagueId);
      if (config) {
        setRegistrationOpen(config.registrationOpen || false);
        setRegistrationOpenDate(config.registrationOpenDate ? config.registrationOpenDate.slice(0, 16) : '');
        setPendingTimeoutMinutes(config.pendingTimeoutMinutes || 15);
        setBlurb(config.blurb || '');
        setPaymentWarningMessage(config.paymentWarningMessage || '');
        setInstallmentsEnabled(config.installmentsEnabled || false);
        setPositions(config.positions?.map(p => ({
          positionType: p.positionType,
          totalSlots: p.totalSlots,
          priceInCents: p.priceInCents,
          filledSlots: p.filledSlots || 0,
        })) || []);
        setEarlyAccessEnabled(config.earlyAccessEnabled || false);
        setEarlyAccessToken(config.earlyAccessToken || null);
        setEarlyAccessExpiry(config.earlyAccessExpiresAt ? config.earlyAccessExpiresAt.slice(0, 16) : '');
        setLeagueSlug(config.leagueSlug || null);
      }
    } catch (err) {
      setError('Failed to load registration configuration.');
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await unifiedApi.upsertRegistrationConfig(leagueId, {
        registrationOpen,
        registrationOpenDate: registrationOpenDate || null,
        pendingTimeoutMinutes,
        blurb: isHtmlEmpty(blurb) ? null : blurb,
        paymentWarningMessage: isHtmlEmpty(paymentWarningMessage) ? null : paymentWarningMessage,
        installmentsEnabled,
        positions: positions.map(p => ({
          positionType: p.positionType,
          totalSlots: p.totalSlots,
          priceInCents: p.priceInCents,
        })),
      });
      setSuccess('Registration configuration saved successfully.');
    } catch (err) {
      const msg = err.message || '';
      if (msg.includes('Cannot remove')) {
        setError('Cannot remove a position that has active registrations.');
      } else {
        setError('Failed to save registration configuration.');
      }
    } finally {
      setSaving(false);
    }
  };

  const addPosition = () => {
    const usedTypes = new Set(positions.map(p => p.positionType));
    const nextType = addablePositionTypes.find(pt => !usedTypes.has(pt.value));
    if (!nextType) return;
    setPositions([...positions, { positionType: nextType.value, totalSlots: 0, priceInCents: 0, filledSlots: 0 }]);
  };

  const removePosition = (index) => {
    setPositions(positions.filter((_, i) => i !== index));
  };

  const updatePosition = (index, field, value) => {
    const updated = [...positions];
    updated[index] = { ...updated[index], [field]: value };
    setPositions(updated);
  };

  const handleGenerateEarlyAccess = async () => {
    setEarlyAccessBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const link = await unifiedApi.generateEarlyAccessLink(leagueId, earlyAccessExpiry || null);
      setEarlyAccessEnabled(link.enabled);
      setEarlyAccessToken(link.token);
      setEarlyAccessExpiry(link.expiresAt ? link.expiresAt.slice(0, 16) : '');
      if (link.leagueSlug) setLeagueSlug(link.leagueSlug);
      setSuccess(earlyAccessToken ? 'Early-access link regenerated. The previous link no longer works.' : 'Early-access link generated.');
    } catch (err) {
      setError('Failed to generate early-access link.');
    } finally {
      setEarlyAccessBusy(false);
    }
  };

  const handleRevokeEarlyAccess = async () => {
    setEarlyAccessBusy(true);
    setError(null);
    setSuccess(null);
    try {
      await unifiedApi.revokeEarlyAccessLink(leagueId);
      setEarlyAccessEnabled(false);
      setEarlyAccessToken(null);
      setEarlyAccessExpiry('');
      setSuccess('Early-access link revoked.');
    } catch (err) {
      setError('Failed to revoke early-access link.');
    } finally {
      setEarlyAccessBusy(false);
    }
  };

  const handleCopyEarlyAccess = async () => {
    if (!earlyAccessUrl) return;
    try {
      await navigator.clipboard.writeText(earlyAccessUrl);
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000);
    } catch (err) {
      setError('Could not copy the link to the clipboard.');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/admin/leagues/${leagueId}`)}
          sx={{ mb: 2 }}
        >
          Back to League Admin
        </Button>
        <Typography variant="h4" gutterBottom>
          Registration Configuration
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

      <Stack spacing={3}>
        {/* Registration Status */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Registration Status</Typography>
          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={registrationOpen}
                  onChange={(e) => setRegistrationOpen(e.target.checked)}
                  color="success"
                  disabled={!canEdit}
                />
              }
              label={registrationOpen ? 'Registration Open' : 'Registration Closed'}
            />

            <TextField
              label="Scheduled Open Date"
              type="datetime-local"
              value={registrationOpenDate}
              onChange={(e) => setRegistrationOpenDate(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              helperText="Optional. Registration won't show until this date."
              fullWidth
              disabled={!canEdit}
              sx={{ maxWidth: 400 }}
            />

            <TextField
              label="Pending Timeout (minutes)"
              type="number"
              value={pendingTimeoutMinutes}
              onChange={(e) => setPendingTimeoutMinutes(parseInt(e.target.value) || 15)}
              helperText="Time before an unpaid registration expires."
              slotProps={{ input: { inputProps: { min: 1, max: 1440 } } }}
              disabled={!canEdit}
              sx={{ maxWidth: 200 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={installmentsEnabled}
                  onChange={(e) => setInstallmentsEnabled(e.target.checked)}
                  disabled={!canEdit}
                />
              }
              label="Allow installment payments"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
              Uses the organization&apos;s installment settings. Turning this off does not affect anyone already on a plan.
            </Typography>
          </Stack>
        </Paper>

        {/* Early-access Link */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Early-Access Link</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Generate a hard-to-guess link that lets people register <strong>before</strong> registration opens.
            The league still appears closed on the public registration page — only people with this link can sign up early.
            Share the same link with as many people as you like. To shut it off, revoke it (any registrations made through it can be deleted and refunded).
          </Typography>

          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={earlyAccessEnabled ? 'Active' : 'Inactive'}
                color={earlyAccessEnabled ? 'success' : 'default'}
                size="small"
                clickable={false}
                onClick={() => {}}
              />
              {earlyAccessEnabled && earlyAccessExpiry && (
                <Typography variant="body2" color="text.secondary">
                  Expires {new Date(earlyAccessExpiry).toLocaleString()}
                </Typography>
              )}
              {earlyAccessEnabled && !earlyAccessExpiry && (
                <Typography variant="body2" color="text.secondary">
                  No expiry — active until revoked
                </Typography>
              )}
            </Box>

            {earlyAccessEnabled && earlyAccessUrl && (
              <TextField
                label="Shareable link"
                value={earlyAccessUrl}
                fullWidth
                slotProps={{
                  input: {
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={copyFeedback ? 'Copied!' : 'Copy link'}>
                          <IconButton onClick={handleCopyEarlyAccess} edge="end" size="small">
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}

            {canEdit && (
              <>
                <TextField
                  label="Optional expiry"
                  type="datetime-local"
                  value={earlyAccessExpiry}
                  onChange={(e) => setEarlyAccessExpiry(e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                  helperText="Leave blank for no expiry. Applied when you generate the link."
                  fullWidth
                  sx={{ maxWidth: 400 }}
                />

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Button
                    variant="contained"
                    startIcon={<VpnKeyIcon />}
                    onClick={handleGenerateEarlyAccess}
                    disabled={earlyAccessBusy}
                  >
                    {earlyAccessEnabled ? 'Regenerate Link' : 'Generate Link'}
                  </Button>
                  {earlyAccessEnabled && (
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<LinkOffIcon />}
                      onClick={handleRevokeEarlyAccess}
                      disabled={earlyAccessBusy}
                    >
                      Revoke Link
                    </Button>
                  )}
                </Stack>
              </>
            )}
          </Stack>
        </Paper>

        {/* Registration Blurb */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Registration Blurb</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Displayed at the top of the public registration page. Use the toolbar to add formatting like bold, italic, lists, and links.
          </Typography>
          <Box sx={{
            '& .ql-container': { minHeight: 150, fontSize: '1rem' },
            '& .ql-editor': { minHeight: 150 },
          }}>
            <ReactQuill
              theme="snow"
              value={blurb}
              onChange={setBlurb}
              modules={quillModules}
              formats={quillFormats}
              readOnly={!canEdit}
              placeholder="e.g., Welcome to the Spring 2026 season! Games are played on Thursdays at 8:00 PM. 7v7 format, 12 games."
            />
          </Box>
        </Paper>

        {/* Payment Warning Message */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Payment Warning Message</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Optional. When set, a confirmation dialog with this message will appear before the user is redirected to payment. Use this to remind registrants which league they are signing up for.
          </Typography>
          <Box sx={{
            '& .ql-container': { minHeight: 100, fontSize: '1rem' },
            '& .ql-editor': { minHeight: 100 },
          }}>
            <ReactQuill
              theme="snow"
              value={paymentWarningMessage}
              onChange={setPaymentWarningMessage}
              modules={quillModules}
              formats={quillFormats}
              readOnly={!canEdit}
              placeholder="e.g., Warning: You are registering for SATURDAY. Please confirm this is the correct league."
            />
          </Box>
        </Paper>

        {/* Position Inventory & Pricing */}
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Position Inventory & Pricing</Typography>
            {canEdit && (
            <Button
              startIcon={<AddIcon />}
              onClick={addPosition}
              disabled={addablePositionTypes.every(pt => positions.some(p => p.positionType === pt.value))}
              size="small"
            >
              Add Position
            </Button>
            )}
          </Box>

          {!sparesEnabled && positions.some(p => SPARE_POSITION_TYPES.has(p.positionType)) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Spares are turned off for this league, so spare positions aren't offered to registrants.
            </Alert>
          )}

          {positions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No positions configured. Add positions to enable registration.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Position</TableCell>
                    <TableCell align="right">Total Slots</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell align="center" width={60}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {positions.map((pos, index) => (
                    <TableRow key={pos.positionType}>
                      <TableCell>{getPositionLabel(pos.positionType)}</TableCell>
                      <TableCell align="right">
                        <TextField
                          type="number"
                          value={pos.totalSlots}
                          onChange={(e) => updatePosition(index, 'totalSlots', parseInt(e.target.value) || 0)}
                          size="small"
                          disabled={!canEdit}
                          slotProps={{ input: { inputProps: { min: 0 } } }}
                          sx={{ width: 100 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          type="number"
                          value={(pos.priceInCents / 100).toFixed(2)}
                          onChange={(e) => updatePosition(index, 'priceInCents', Math.round(parseFloat(e.target.value || 0) * 100))}
                          size="small"
                          disabled={!canEdit}
                          slotProps={{
                            input: {
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                              inputProps: { min: 0, step: 0.01 },
                            },
                          }}
                          sx={{ width: 140 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {canEdit && (
                        <Tooltip title={pos.filledSlots > 0 ? 'Cannot delete — has active registrations' : ''}>
                          <span>
                            <IconButton size="small" onClick={() => removePosition(index)} color="error" disabled={pos.filledSlots > 0}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* Save Button */}
        {canEdit && (
        <Box>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            size="large"
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </Box>
        )}
      </Stack>
    </AdminLayout>
  );
};

export default RegistrationConfigPage;
